export const CATEGORY_META = {
  photovoltaic: {
    title: "Photovoltaic Solar Energy",
    description: "Clean solar electricity for homes, businesses, and utilities — off-grid, on-grid, and full project delivery.",
    color: "teal",
    icon: "☀️",
  },
  realestate: {
    title: "Smart & Green Real Estate",
    description: "Eco-friendly buildings with integrated renewable energy, efficient design, and intelligent controls.",
    color: "green",
    icon: "🏢",
  },
  emobility: {
    title: "E-Mobility Services",
    description: "Charging, fleet electrification, and advisory for Rwanda’s clean transport future.",
    color: "blue",
    icon: "⚡",
  },
};

const COLOR_CYCLE = ["teal", "green", "blue"];
const ACRONYMS = new Set(["DRC", "UK", "USA", "UAE", "UN", "EU"]);

export function prettyLabel(slug) {
  if (CATEGORY_META[slug]) return CATEGORY_META[slug].title;
  return String(slug || "")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function categoryKey(value) {
  return String(value || "general").toLowerCase().trim();
}

export function prettyCountry(value) {
  const raw = String(value || "").trim().replace(/\s+/g, " ");
  if (!raw) return "Other";
  return raw
    .split(" ")
    .map((word) => {
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function groupServices(items = []) {
  const grouped = {};
  items.forEach((item, index) => {
    const key = categoryKey(item.category);
    if (!grouped[key]) {
      const meta = CATEGORY_META[key] || {
        title: prettyLabel(key),
        description: `RENERG ${prettyLabel(key)} solutions for homes, businesses, and communities.`,
        color: COLOR_CYCLE[Object.keys(grouped).length % COLOR_CYCLE.length],
        icon: item.icon || "☀️",
      };
      grouped[key] = { key, ...meta, services: [] };
    }
    grouped[key].services.push({ ...item, key: item.id || `${key}-${index}` });
  });
  return grouped;
}

export function groupProjectsByCountry(items = []) {
  const grouped = {};
  items.forEach((item) => {
    const country = prettyCountry(item.country);
    if (!grouped[country]) grouped[country] = [];
    grouped[country].push(item);
  });
  return Object.fromEntries(Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)));
}

export function countryMarkets(items = []) {
  return Object.entries(groupProjectsByCountry(items)).map(([country, list]) => {
    const cover = list.find((p) => p.imageUrl)?.imageUrl || "";
    const locations = [...new Set(list.map((p) => p.location).filter(Boolean))];
    return {
      country,
      count: list.length,
      cover,
      locations,
      hint: locations.slice(0, 2).join(" · ") || `${list.length} delivery${list.length === 1 ? "" : "ies"}`,
    };
  });
}

function plainText(html) {
  return String(html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function wrapHtml(text) {
  const raw = String(text || "").trim();
  if (!raw) return "";
  if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
  return `<p>${raw}</p>`;
}

const SERVICE_PACKS = {
  photovoltaic: {
    indexKicker: "What we build",
    indexTitle: "One team, every kind of solar system",
    indexLead: "Whatever the scale, the engineering is the same: sized to your real load, built with bankable equipment, and handed over documented.",
    who: "Homes, farms, clinics, hotels, offices, churches and public buildings — across Rwanda and the markets where RENERG already builds.",
    whoTitle: "Built for the places power matters most",
    places: "Rooftops, lodges, health posts, factories and community sites. The same team sizes the plant, installs it and stays after handover.",
    getTitle: "Everything handled, end to end",
    getLead: "From the first site visit to the day the system switches on — and the years after.",
    quote: "Sized for your load, not for a catalogue.",
    quoteNote: "Every system starts with a real measurement of what you use — so you pay for the capacity you need.",
    stepsTitle: "Four steps from first visit to working power",
    get: [
      ["Needs assessment and sizing", "Sized against your actual load and budget before anything is bought or installed."],
      ["Bankable equipment", "Panels, inverters and structures selected for the local climate with spare-parts access."],
      ["Installation and testing", "Professional installation, full system testing, handover documents and operator briefing."],
      ["Optional maintenance", "Scheduled service so performance does not drop after year one."],
    ],
    steps: [
      ["Site survey and load-based sizing", "We visit the site, measure the real load and size the system against your budget."],
      ["Quality panels, inverters, structure and cabling", "Only bankable equipment with spare-parts access goes into the design."],
      ["Off-grid, hybrid or grid-tied design", "The system is engineered for your situation — with or without the grid, with storage as needed."],
      ["Installation, testing and documented handover", "Certified installation, full testing and clear handover documents and operator briefing."],
    ],
    glance: [
      ["Typical sizes", "Small rooftops through commercial plants"],
      ["Modes", "Off-grid, on-grid, or hybrid with storage"],
      ["Life of plant", "20–25 years with scheduled service"],
    ],
  },
  realestate: {
    indexKicker: "What we build",
    indexTitle: "Buildings planned around how they are used",
    indexLead: "Energy, daylight, water and controls are part of the design, then carried through to a documented handover.",
    who: "Developers, hotels, churches, schools and families — new buildings and retrofits across Rwanda.",
    whoTitle: "Built for the places people live and work",
    places: "The brief starts with how the building is occupied, then solar, water and comfort are sized to that use.",
    getTitle: "Everything handled, from design to handover",
    getLead: "From the first brief to the day the owner takes the keys — and the upkeep after.",
    quote: "Designed for the building in use, not for a catalogue.",
    quoteNote: "We measure how the place is lived in, then size energy and water to that, not to a standard package.",
    stepsTitle: "Four steps from first brief to a finished building",
    get: [
      ["Energy in the design", "Solar, daylight and water are drawn into the concept, not added after the walls are up."],
      ["Materials for this climate", "Insulation and finishes chosen for heat, rain and the way the building is occupied."],
      ["Build and commission", "Site supervision, testing and a handover pack the owner can keep."],
      ["What to maintain", "A short note of what must be serviced so bills stay where the offer said they would."],
    ],
    steps: [
      ["Brief and site", "You share the plot or the existing building and how people will use it."],
      ["Target and budget", "We set the energy target, the cost and the decisions that cannot slip."],
      ["Design, supply and build", "One sequence, so solar, water and the building stay aligned."],
      ["Handover", "Drawings, settings and a short briefing for the owner."],
    ],
    glance: [
      ["Focus", "Lower bills, comfort and a smaller footprint"],
      ["Works on", "New build and retrofit"],
      ["Integrates", "Solar, lighting, water and controls"],
    ],
  },
  emobility: {
    indexKicker: "What we build",
    indexTitle: "Charging that fits the supply you already have",
    indexLead: "Home, workplace and depot charging, checked against the existing power so the rest of the site stays on.",
    who: "Households, companies and fleets — at home, at the office, or in a yard where vehicles park.",
    whoTitle: "Built for the places vehicles actually park",
    places: "We start from how many vehicles charge, and when, then size the circuit so the building supply holds.",
    getTitle: "Everything handled, from supply check to daily use",
    getLead: "From the first look at your meter to the day drivers plug in — and when the fleet grows.",
    quote: "Sized for your vehicles, not for a catalogue.",
    quoteNote: "Every charger starts from the supply on site and the hours the vehicles are parked.",
    stepsTitle: "Four steps from first visit to a working charger",
    get: [
      ["Load study", "Charging is checked against the rest of the site so it does not trip the supply."],
      ["Layout for the parking you have", "Home, workplace or depot positions, labelled for daily use."],
      ["Installation and briefing", "Chargers tested, and drivers or technicians shown how they run."],
      ["Room to grow", "Solar, storage or another vehicle can be added without starting again."],
    ],
    steps: [
      ["Vehicles and hours", "You tell us how many vehicles park here and when they need to charge."],
      ["Supply and budget", "We check the meter, then agree the circuit, the power and the price."],
      ["Install and label", "Chargers are fitted, tested and marked for the people who use them."],
      ["After the first week", "We stay available when another vehicle or a solar plant is added."],
    ],
    glance: [
      ["Sites", "Homes, offices, hotels and depots"],
      ["Power", "Domestic circuits through three-phase yards"],
      ["Option", "Solar and storage to cut the cost of charging"],
    ],
  },
};

function servicePack(item) {
  return SERVICE_PACKS[categoryKey(item?.category)] || {
    indexKicker: "What we build",
    indexTitle: "A clear scope, then a documented handover",
    indexLead: "Each service is sized to the site, written into an offer, and handed over with a named contact.",
    who: "Clients who want a practical RENERG delivery, with a written scope and a team that stays after handover.",
    whoTitle: "Built for the site in front of us",
    places: "Homes, businesses and public sites in Rwanda and partner countries.",
    getTitle: "Everything handled, end to end",
    getLead: "From the first visit to handover, and the support after.",
    quote: "Sized for this site, not for a catalogue.",
    quoteNote: "The offer follows what we measure, so you pay for the work you need.",
    stepsTitle: "Four steps from first visit to handover",
    get: [
      ["A clear scope", "Price, equipment and exclusions are written before work starts."],
      ["Equipment you can service", "Chosen for local spare parts, not a one-off import."],
      ["Installation and documents", "Test results and a handover pack you keep."],
      ["A named contact", "The same team answers after the work is complete."],
    ],
    steps: [
      ["Tell us the site", "Send what you need the system to do."],
      ["Visit and offer", "We confirm the scope and send a written price."],
      ["Install and test", "The work is commissioned against that offer."],
      ["Stay available", "Support remains open after the first weeks of use."],
    ],
    glance: [["Start", "Share the site and the load on the contact form"]],
  };
}

export function serviceStory(item) {
  const pack = servicePack(item);
  const title = item?.title || "this service";
  return {
    indexKicker: pack.indexKicker,
    indexTitle: pack.indexTitle,
    indexLead: pack.indexLead,
    intro: plainText(item?.description) || `${title} is sized to the site, written into an offer, and handed over with documents.`,
    getTitle: pack.getTitle,
    getLead: pack.getLead,
    get: pack.get,
    quote: pack.quote,
    quoteNote: pack.quoteNote,
    stepsTitle: pack.stepsTitle,
    steps: pack.steps,
    who: pack.who,
    whoTitle: pack.whoTitle,
    places: pack.places,
  };
}

export function defaultServiceHtml(item) {
  const title = item?.title || "this service";
  const cat = CATEGORY_META[categoryKey(item?.category)]?.title || prettyLabel(item?.category);
  const pack = servicePack(item);
  const intro = wrapHtml(item?.description) || `<p>RENERG delivers ${title} as part of our ${cat} work, designed for the sites we already know and for new markets that need the same standard.</p>`;
  return `${intro}
<p>${title} is scoped, supplied and commissioned by one team. You see the offer before equipment is ordered, and you keep a contact after the system is in use.</p>
<h2>Who this is for</h2>
<p>${pack.who}</p>
<p>If ${title} is the wrong fit, we say so before you pay for a design.</p>
<h2>What you receive</h2>
<p>The offer names the equipment, the programme and what you need to prepare on site. ${title} is not left as a box of parts with no owner.</p>
<ul>
${pack.get.map(([title, text]) => `<li><strong>${title}.</strong> ${text}</li>`).join("\n")}
</ul>
<h2>How the work runs</h2>
<p>Each step has an owner. You know when we need a decision, and when the crew is on site.</p>
<ul>
${pack.steps.map(([title, text]) => `<li><strong>${title}.</strong> ${text}</li>`).join("\n")}
</ul>
<h2>Where it is used</h2>
<p>${pack.places}</p>
<p>The design allows for rain, dust, voltage swings and the way people actually use the building or the vehicles.</p>
<h2>At a glance</h2>
<ul>
${pack.glance.map(([k, v]) => `<li><strong>${k}:</strong> ${v}</li>`).join("\n")}
</ul>
<h2>After handover</h2>
<p>You keep the drawings, the test sheet and the settings. We check early performance against the offer so ${title} is still producing after the first season, not only on the day of commissioning.</p>
<p>Spare parts and a maintenance visit can be agreed in the same contract or added later. Growth of the load, the building or the fleet is planned as a next step, not a surprise.</p>
<h2>Questions we hear about ${title}</h2>
<h3>How do we start?</h3>
<p>Send the contact form with the location and a short note on the load, the building or the vehicles. We reply with a proposed visit and what we need from you before that day.</p>
<h3>What does the offer include?</h3>
<p>Scope, main equipment, programme and price. Exclusions are written down so ${title} does not grow in silence after you have accepted.</p>
<h3>Can this sit with other RENERG work?</h3>
<p>Yes. ${cat} is often delivered with solar, a building upgrade or charging on the same site. We say which parts belong in this contract and which belong in the next one.</p>
<h3>Who do we call after it is live?</h3>
<p>The same RENERG contact that followed the job. You are not passed to an unnamed inbox.</p>`;
}

export function defaultProjectHtml(item) {
  const title = item?.title || "this project";
  const country = prettyCountry(item?.country);
  const place = [item?.location, country].filter(Boolean).join(", ");
  const capacity = item?.capacity ? ` with ${item.capacity} of installed capacity` : "";
  const intro = wrapHtml(item?.description) || `<p>${title} is a RENERG delivery in ${place}${capacity}.</p>`;
  return `${intro}
<h2>Project snapshot</h2>
<ul>
<li><strong>Country:</strong> ${country} — listed in the website Projects menu under this market</li>
<li><strong>Location:</strong> ${item?.location || "Client site"}</li>
<li><strong>Capacity:</strong> ${item?.capacity || "Sized to the client load"}</li>
<li><strong>Status:</strong> ${String(item?.status || "ONGOING").replace(/_/g, " ")}</li>
</ul>
<h2>How RENERG delivered it</h2>
<p>The work followed our standard sequence: site data, engineering, procurement, installation and commissioning. Local crews worked under RENERG supervision so quality matches our Rwanda reference plants.</p>
<p>After handover, operators received a short briefing. A maintenance option remains available so ${title} keeps producing through dusty seasons, outages and load growth.</p>
<h2>Ask for a similar plant</h2>
<p>If you have a site in ${country} or another market, share your load and roof or land area. We will say honestly what is practical and what it should cost.</p>`;
}

export function serviceBody(item) {
  const existing = item?.body || "";
  const headings = (String(existing).match(/<h2\b/gi) || []).length;
  if (plainText(existing).length > 700 && headings >= 5) return existing;
  return defaultServiceHtml(item);
}

export function projectBody(item) {
  const existing = item?.body || "";
  if (plainText(existing).length > 60) return existing;
  return defaultProjectHtml(item);
}
